import { useEffect, useRef, useState } from "react";
import { PiBellLight } from "react-icons/pi";
import client from "../../api/client";

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const dropRef = useRef(null);
  const btnRef = useRef(null);
  const [pos, setPos] = useState(null);

  const authHeader = () => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
  };

  const loadUnreadCount = () => {
    authHeader();
    client.get("/notifications/unread-count")
      .then(res => setUnread(res.data?.unread_count || 0))
      .catch(() => {});
  };

  const loadList = () => {
    setLoading(true);
    authHeader();
    client.get("/notifications")
      .then(res => {
        const items = res.data?.data || [];
        setNotifications(items);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadUnreadCount(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  // Fixed to the viewport, opening rightward from the bell (it sits in the left sidebar, which would
  // clip it), and kept on screen on narrow widths.
  const place = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.min(340, window.innerWidth - 16);
    setPos({ top: r.bottom + 8, left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)), width });
  };
  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [open]);

  const handleOpen = () => {
    const opening = !open;
    if (opening) place();
    setOpen(opening);
    if (opening) {
      loadList();
      if (unread > 0) {
        authHeader();
        client.post("/notifications/mark-all-as-read").catch(() => {});
        setUnread(0);
      }
    }
  };

  return (
    <div className="relative" ref={dropRef}>
      <button
        ref={btnRef}
        onClick={handleOpen}
        className="relative flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700"
        aria-label="Notifications"
      >
        <PiBellLight size={20} />
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full flex items-center justify-center leading-none">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden" style={{ top: pos?.top ?? 64, left: pos?.left ?? 8, width: pos?.width ?? 320, zIndex: 80 }}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-bold text-gray-900">Notifications</h3>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="p-4 space-y-3">
                {[1,2,3].map(i => <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />)}
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-2xl mb-2">🔔</p>
                <p className="text-xs text-gray-400">No recent activity</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-50">
                {notifications.map((n) => (
                  <li key={n.id} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                    <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-sm flex-shrink-0 mt-0.5">
                      💬
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-800 leading-snug">{n.data?.title}</p>
                      <p className="text-xs text-gray-700 leading-snug">{n.data?.message}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">{timeAgo(n.created_at)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="px-4 py-2.5 border-t border-gray-100 flex justify-end">
            <button
              onClick={loadList}
              className="text-xs text-[#294a93] font-semibold hover:underline"
            >
              Refresh
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
